import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, LibraryBig, PencilLine } from "lucide-react";
import { ExerciseLibraryPicker } from "@/components/ExerciseLibraryPicker";
import { toRoutineExercise } from "@/lib/exerciseLibrary";

export function EditExerciseDialog({ open, onOpenChange, exercise, onSave }) {
  const [name, setName] = useState("");
  const [muscleGroup, setMuscleGroup] = useState("");
  const [sets, setSets] = useState("3");
  const [reps, setReps] = useState("8-10");

  const isEdit = !!exercise;

  useEffect(() => {
    if (open) {
      setName(exercise?.name || "");
      setMuscleGroup(exercise?.muscleGroup || "");
      setSets(exercise?.sets ? String(exercise.sets) : "3");
      setReps(exercise?.reps || "8-10");
    }
  }, [open, exercise]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const s = parseInt(sets, 10);
    onSave({
      id: exercise?.id,
      name: trimmed,
      muscleGroup: muscleGroup.trim() || "General",
      sets: isNaN(s) || s < 1 ? 1 : s,
      reps: reps.trim() || "8-10",
    });
    onOpenChange(false);
  };

  // Un ejercicio de la biblioteca ya trae nombre, grupo muscular y animación:
  // se añade de una vez, sin pasar por el formulario.
  const handlePickFromLibrary = (libraryExercise) => {
    onSave(toRoutineExercise(libraryExercise, { id: exercise?.id }));
    onOpenChange(false);
  };

  const manualForm = (
    <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">
              Nombre
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Press Banca"
              className="h-12 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">
              Grupo muscular
            </Label>
            <Input
              value={muscleGroup}
              onChange={(e) => setMuscleGroup(e.target.value)}
              placeholder="Pecho, Espalda, Pierna..."
              className="h-12 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">
                Series
              </Label>
              <Input
                type="number"
                value={sets}
                onChange={(e) => setSets(e.target.value)}
                placeholder="4"
                className="h-12 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-[0.08em]">
                Repeticiones
              </Label>
              <Input
                value={reps}
                onChange={(e) => setReps(e.target.value)}
                placeholder="8-10"
                className="h-12 rounded-xl bg-secondary/30 border-border/40 text-foreground font-medium focus-visible:ring-primary/40 placeholder:text-muted-foreground/40"
              />
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={!name.trim()}
            className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-bold text-sm gap-2 fp-glow hover:bg-primary/90 transition-colors duration-200 disabled:opacity-40 disabled:shadow-none mt-2"
          >
            <Check className="h-4 w-4" />
            Guardar
          </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-surface rounded-3xl border-0 w-[calc(100vw-2rem)] max-w-md p-0 overflow-hidden">
        <div className="p-6 pb-0">
          <DialogHeader className="text-left">
            <DialogTitle className="text-foreground text-xl font-bold tracking-tight">
              {isEdit ? "Editar Ejercicio" : "Añadir Ejercicio"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {isEdit
                ? "Modifica los datos del ejercicio."
                : "Elígelo de la biblioteca o créalo a mano."}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Al editar no hay biblioteca: sustituir el ejercicio por otro es
            justamente lo que hace "añadir", no "editar". */}
        {isEdit ? (
          <div className="p-6 pt-4">{manualForm}</div>
        ) : (
          // min-w-0 en toda la cadena: DialogContent es un grid, y sus
          // descendientes tienen min-width:auto, así que sin esto el contenido
          // ancho estira el diálogo en vez de recortarse o hacer scroll.
          <Tabs defaultValue="biblioteca" className="w-full min-w-0">
            <div className="px-6 pt-4">
              <TabsList className="w-full bg-secondary/30 rounded-xl h-10 p-1">
                <TabsTrigger
                  value="biblioteca"
                  className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary gap-1.5"
                >
                  <LibraryBig className="h-3.5 w-3.5" />
                  Biblioteca
                </TabsTrigger>
                <TabsTrigger
                  value="manual"
                  className="flex-1 rounded-lg text-xs font-semibold data-[state=active]:bg-primary/15 data-[state=active]:text-primary gap-1.5"
                >
                  <PencilLine className="h-3.5 w-3.5" />
                  Manual
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="biblioteca" className="p-6 pt-4 mt-0 min-w-0">
              <ExerciseLibraryPicker onPick={handlePickFromLibrary} />
            </TabsContent>

            <TabsContent value="manual" className="p-6 pt-4 mt-0 min-w-0">
              {manualForm}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
